import { tz } from '@date-fns/tz'
import { endOfDay, format, parseISO, startOfDay } from 'date-fns'

/**
 * UTC-day helpers for feeds whose rows are keyed to a UTC calendar day.
 *
 * date-fns v4 takes a timezone context as an option, so none of this needs manual
 * millisecond arithmetic — these are thin adapters that return numbers, which is
 * what the request builders and query keys want.
 */
export const UTC = tz('UTC')

/** The UTC midnight opening the UTC day that contains `ms`. */
export const utcDayStartMs = (ms: number): number => startOfDay(ms, { in: UTC }).getTime()

/**
 * The last millisecond of that same UTC day.
 *
 * Needed wherever a picked end date has to *include* the day the user chose: on a
 * timestamp-granular feed, sending the day's opening midnight as `to` would cut the
 * whole of that day off the window.
 */
export const utcDayEndMs = (ms: number): number => endOfDay(ms, { in: UTC }).getTime()

/**
 * A day the user clicked in a calendar → the UTC midnight that names that day.
 *
 * The calendar deals in local days while the rows are keyed to UTC midnight, so
 * `startOfDay` on the picked Date is the wrong conversion: west of Greenwich it
 * lands on the previous UTC day and quietly drops the day actually chosen.
 * Formatting the local day and reading it back as UTC keeps "the day they clicked"
 * and "the day we ask for" the same day everywhere on earth.
 */
export const utcDayMsFromCalendarDate = (date: Date): number =>
  parseISO(`${format(date, 'yyyy-MM-dd')}T00:00:00Z`).getTime()

/** The inverse, for handing a stored UTC day back to a calendar to highlight. */
export const calendarDateFromUtcDayMs = (ms: number): Date =>
  parseISO(format(ms, 'yyyy-MM-dd', { in: UTC }))
